import { toFinancierTabStatus } from "../utils/tabStatus";
import apiClient from "./client";
import { ENDPOINTS } from "./endpoints";
import { getUserIds } from "./session";

export const getDashboardBuckets = () =>
  apiClient.post(ENDPOINTS.dashboard.getDashboardBuckets, {
    source: "SFA",
    ...getUserIds(),
    screen_type: "sfa_dashboard",
  });

// get_leads pages at a fixed PAGE_LIMIT (6) server-side — callers must page
// through pageNum (1-based) using the response's `nextpage` flag rather than
// expecting one page to return everything.
// requestType echoes a bucket's filter_params.request_type (e.g. "my_leads")
// back to the listing so it applies the SAME row-scoping rule the bucket's
// count used — some buckets (My Leads, Reportee Applications) scope by more
// than status_id/sub_status_id (e.g. "leads assigned to me"), and status/
// sub-status filters alone can't express that.
export const getLeads = ({ statusId, subStatusId, search, requestType, pageNum } = {}) =>
  apiClient.post(ENDPOINTS.dashboard.getLeads, {
    source: "SFA",
    ...getUserIds(),
    page_num: pageNum || 1,
    ...(statusId?.length ? { status_id: statusId } : {}),
    ...(subStatusId?.length ? { sub_status_id: subStatusId } : {}),
    ...(search ? { search } : {}),
    ...(requestType ? { request_type: requestType } : {}),
  });

// sfa_dashboard's buckets_data is sectioned ({title, type, data}[]) so buckets
// can be grouped/hidden per role; other screens (web_dashboard/loan_summary)
// return a flat bucket list directly. Flatten to one ordered array either way,
// preserving screen order, so callers can index into it positionally.
export const flattenBuckets = (bucketsData) => {
  if (!Array.isArray(bucketsData)) return [];
  if (bucketsData.length && Array.isArray(bucketsData[0]?.data)) {
    return bucketsData.flatMap((section) => section.data || []);
  }
  return bucketsData;
};

// Maps a get_leads `lead_info` row to the shape the dashboard lead-card needs.
// `rawId` is the numeric rem_lead.id (required by getLeadDetails/add_financier/
// etc — the same raw-id-for-API-calls convention as leadApi's loanId), while
// `loan` is the REM<id> display code already used everywhere else.
export const toLeadCard = (row) => ({
  rawId: row.id,
  loan: row.loan_code || (row.id ? "REM" + row.id : ""),
  name: row.customer?.name || "—",
  date: row.created_date ? new Date(row.created_date).toLocaleDateString() : "",
  // sourceType is the lead_source category label (Agent/Website/Individual/...);
  // sourceName is the actual partner/dealer name and is genuinely blank for
  // source types with no partner attached (e.g. Website, Individual) — that's
  // real data, not a mapping gap.
  sourceType: row.lead_source?.lead_source_name || "—",
  sourceName: row.partner_name || "",
  // The application submitter's name (SO, falling back to SM) — shown on
  // every lead card.
  submittedBy: row.user_name || "",
  status: row.sub_status_name || row.status_name || "",
  // Numbers, whatever the API sends ("2" vs 2) — statusStage / pillBg /
  // the CTA label all compare these strictly.
  statusId: row.status_id != null ? Number(row.status_id) : null,
  subStatusId: row.sub_status_id != null ? Number(row.sub_status_id) : null,
  isCompleteApplicationCta: !!row.is_complete_application_cta,
  // Master switch — false means this lead belongs to someone else (e.g. a
  // reportee's, viewed via the Reportee Applications bucket): no action CTA,
  // no click-through into the application/post-application flow at all.
  isSameUser: !!row.is_same_user,
  // Whether MORE financiers can still be added (not frozen, under the cap) —
  // gates step 2's bank grid staying interactive after some are already saved.
  enableAddFinancier: !!row.enable_add_financier,
  // Everything on this lead is done — the whole form becomes read-only.
  isFreeze: !!row.is_freeze,
  // Which step the user last saved — resuming should land there directly
  // instead of always starting over at step 1.
  lastFormFilled: row.last_form_filled || null,
  // Per-financier tab_status → drives which Send / NOA / Contract actions the
  // post-application page allows for each bank.
  financierTabStatus: toFinancierTabStatus(row),
});
